import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Input, Divider, FormInstance, Form } from 'antd'
import { FormItem, theme, Select, Button } from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Option } from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { getRequiredRule } from 'screens/Rostering/utils'
import { Add } from '@lmc/ui-kit'
import { getAddressTypeOptions } from './utils'
import { PostcodeAutofill } from 'screens/Residents/screens/ResidentProfileScreen/screens/ResidentContactDetails/FormCards/Address/PostocdeAutofill'

type AddressFormItemsProps = {
  addressOptions?: any[]
  selectedResident: string
  preferredPeopleNominative?: string
  disabled?: boolean
  formData?: any
  setFormData?: (data: any) => void
  form: FormInstance
  defaultFirstAddress?: boolean
}

const AddressFormItems: FC<AddressFormItemsProps> = ({
  addressOptions = [],
  selectedResident,
  preferredPeopleNominative,
  disabled,
  formData,
  setFormData,
  form,
  defaultFirstAddress,
}) => {
  const { t } = useTranslation()
  const [showAddressForm, setShowAddressForm] = useState(false)
  const [isAddingAddressManually, setIsAddingAddressManually] = useState(false)

  const addressId = Form.useWatch('addressId', form)

  const filterOption = (input: string, option?: Option<string>) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const handleAddNewAddress = () => {
    setShowAddressForm(true)
  }

  const handleFieldChange = (field: string, value: any) => {
    const currentNewAddress = form.getFieldValue('newAddress') || {}

    const updatedAddress = {
      ...currentNewAddress,
      [field]: value,
    }

    form.setFieldsValue({
      newAddress: updatedAddress,
    })

    if (setFormData) {
      setFormData((prev: any) => ({
        ...prev,
        newAddress: updatedAddress,
      }))
    }
  }

  const handleAutofill = (address: any) => {
    const formattedAddress = {
      type: formData?.newAddress?.type || address.type,
      name: address.name || '',
      street1: address.street1 || '',
      street2: address.street2 || '',
      suburb: address.suburb || '',
      state: address.state || '',
      postcode: address.postcode || '',
    }

    form.setFieldsValue({
      newAddress: formattedAddress,
    })

    if (setFormData) {
      setFormData((prev: any) => ({
        ...prev,
        newAddress: formattedAddress,
      }))
    }
  }

  useEffect(() => {
    if (
      defaultFirstAddress &&
      addressOptions?.length > 0 &&
      selectedResident &&
      !addressId
    ) {
      const firstAddress = addressOptions[0]

      form.setFieldValue('addressId', firstAddress.value)

      if (setFormData) {
        setFormData((prev: any) => ({
          ...prev,
          addressId: firstAddress.value,
        }))
      }
    }
  }, [
    addressOptions,
    selectedResident,
    addressId,
    defaultFirstAddress,
    form,
    setFormData,
  ])

  const dropdownRender = (menu: React.ReactElement) => (
    <div>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <StyledButton
        variant="text"
        onClick={handleAddNewAddress}
        disabled={disabled || !selectedResident}
      >
        <Add />
        {t('ADD_NEW_ADDRESS')}
      </StyledButton>
    </div>
  )

  return (
    <>
      {!showAddressForm ? (
        <Tooltip
          color={theme.color.neutral.color3}
          title={
            disabled ? (
              <ContentContainer>
                <Text>{t('DETAIL_CANNOT_BE_EDITED')}</Text>
              </ContentContainer>
            ) : (
              ''
            )
          }
          open={disabled ? undefined : false}
          overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
        >
          <FormItem
            name="addressId"
            label={<Text>{t('ADDRESS')}</Text>}
            rules={[getRequiredRule(t('ADDRESS'))]}
          >
            <Select
              options={addressOptions}
              allowClear
              optionFilterProp="children"
              filterOption={filterOption}
              showSearch
              disabled={disabled}
              dropdownRender={dropdownRender}
            />
          </FormItem>
        </Tooltip>
      ) : (
        <>
          <FormItem
            name={['newAddress', 'type']}
            label={<Text>{t('ADDRESS_TYPE')}</Text>}
            rules={[getRequiredRule(t('ADDRESS_TYPE'))]}
          >
            <Select
              options={getAddressTypeOptions(t)}
              onChange={(value) => handleFieldChange('type', value)}
            />
          </FormItem>

          <ShouldShow $shouldShow={isAddingAddressManually}>
            <AddressFieldsContainer>
              <FormItem
                name={['newAddress', 'name']}
                label={<Text>{t('BUILDING_NAME')}</Text>}
              >
                <Input
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                />
              </FormItem>

              <FormItem
                name={['newAddress', 'street1']}
                label={<Text>{t('ADDRESS_LINE_1')}</Text>}
                rules={[getRequiredRule(t('ADDRESS_LINE_1'))]}
              >
                <Input
                  onChange={(e) => handleFieldChange('street1', e.target.value)}
                />
              </FormItem>

              <FormItem
                name={['newAddress', 'street2']}
                label={<Text>{t('ADDRESS_LINE_2')}</Text>}
              >
                <Input
                  onChange={(e) => handleFieldChange('street2', e.target.value)}
                />
              </FormItem>

              <FormItem
                name={['newAddress', 'suburb']}
                label={<Text>{t('TOWN')}</Text>}
                rules={[getRequiredRule(t('TOWN'))]}
              >
                <Input
                  onChange={(e) => handleFieldChange('suburb', e.target.value)}
                />
              </FormItem>

              <FormItem
                name={['newAddress', 'state']}
                label={<Text>{t('COUNTY')}</Text>}
              >
                <Input
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                />
              </FormItem>

              <FormItem
                name={['newAddress', 'postcode']}
                label={<Text>{t('POSTCODE')}</Text>}
                rules={[getRequiredRule(t('POSTCODE'))]}
              >
                <Input
                  onChange={(e) =>
                    handleFieldChange('postcode', e.target.value)
                  }
                />
              </FormItem>
            </AddressFieldsContainer>
          </ShouldShow>

          {!isAddingAddressManually && (
            <PostcodeAutofill
              onAutofill={handleAutofill}
              fieldName={['newAddress']}
              onManuallyChoose={() => setIsAddingAddressManually(true)}
              isAddingAddressManually={isAddingAddressManually}
            />
          )}
        </>
      )}
    </>
  )
}

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const AddressFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ShouldShow = styled.div<{ $shouldShow: boolean }>`
  display: ${(props) => (props.$shouldShow ? 'contents' : 'none')};
`

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
`

export default AddressFormItems
